import React from "react";
import { Calendar, FileText, CheckCircle } from "lucide-react";
import { DD_MM_YYYY } from "src/helpers";
import { POConfirmation, POAdditionalInfo } from "src/types";
import { NoteDisplay } from "src/components";
import { PercentageBar } from "src/components/ui/percentageBar";
import { StatusPill } from "src/components/ui";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderConfirmationSectionProps extends POConfirmation {
  confirmationNote?: POAdditionalInfo["confirmationNote"];
}

const PurchaseOrderConfirmationSection: React.FC<PurchaseOrderConfirmationSectionProps> = ({
  confirmationStatus,
  confirmedAt,
  confirmationDetails,
  confirmationNote
}) => {
  if (!confirmationStatus || !confirmationDetails) {
    return null;
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={true}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-success-600" />
            Confirmation Details
          </h3>
        }
      >
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-secondary-700">Status</span>
            <StatusPill status={confirmationStatus} />
          </div>

          {/* Fulfilment Rate */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-secondary-800 mb-3">Fulfilment Rate</label>
            <PercentageBar percentage={confirmationDetails.fulfilmentRate} width="100%" />
          </div>

          {/* Dates Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {confirmationDetails.estShipDate && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Ship Date</span>
                </div>
                <p className="text-base font-medium text-secondary-900">
                  {DD_MM_YYYY.formatDate(confirmationDetails.estShipDate)}
                </p>
              </div>
            )}

            {confirmationDetails.estDeliveryDate && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Delivery Date
                  </span>
                </div>
                <p className="text-base font-medium text-secondary-900">
                  {DD_MM_YYYY.formatDate(confirmationDetails.estDeliveryDate)}
                </p>
              </div>
            )}

            {confirmationDetails.estCollectionDate && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Collection Date
                  </span>
                </div>
                <p className="text-base font-medium text-secondary-900">
                  {DD_MM_YYYY.formatDate(confirmationDetails.estCollectionDate)}
                </p>
              </div>
            )}
          </div>

          {/* Confirmation Note */}
          {confirmationNote?.body && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-2">Vendor Note</p>
                  <div className="text-sm text-secondary-700 leading-relaxed">
                    <NoteDisplay note={confirmationNote} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmed By Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-secondary-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-2">Confirmed By</p>
              <p className="text-sm font-medium text-secondary-900">{confirmationDetails.confirmedByEmail}</p>
            </div>
            {confirmedAt && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-2">Date Confirmed</p>
                <p className="text-sm font-medium text-secondary-900">{DD_MM_YYYY.formatDate(confirmedAt)}</p>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderConfirmationSection };
